import confetti from 'canvas-confetti';
import { readSaved, save, savedScore } from './storage.js';
import './shrine-extras.js';
// Jim Sucks — main interactive behaviors
import { activatePartyMode } from './party-modes.js';

// --- Tunables -------------------------------------------------------------
const SPECIAL_ANIMATION_CLICKS = 10; // Jim spins + floats at this many clicks
const PARTY_MODE_CLICKS = 20;        // party mode fires (and counter resets) here
const INCIDENT_RESET_MS = 5000;      // "days since last incident" reshuffles on this cadence
const ISSUES_PER_PARTY = 3;          // party mode fires every Nth logged defect

// --- Cached DOM ----------------------------------------------------------
const jimImg = document.getElementById('jim-img');
const clickCounter = document.getElementById('click-counter');
const clickDisplay = document.getElementById('click-count-display');
const issueCount = document.getElementById('issue-count');
const daysCounter = document.getElementById('days-counter');
const jimAudio = document.getElementById('jim-audio');
const issuesList = document.getElementById('jim-issues');
const addIssueBtn = document.getElementById('add-issue-btn');
const mainTitle = document.getElementById('main-title');

// --- State ---------------------------------------------------------------
let jimClickCount = 0;
let totalJimClicks = savedScore('jim-total-clicks');
let konamiSequence = [];
const konamiCode = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'KeyB', 'KeyA'];

// Additional Jim issues for random adding
const additionalJimIssues = [
    { text: "Still thinks honking solves traffic", severity: "high" },
    { text: "Probably has truck nuts", severity: "medium" },
    { text: "Listens to music too loudly", severity: "low" },
    { text: "Uses turn signals incorrectly", severity: "medium" },
    { text: "Drives in the left lane going slow", severity: "high" },
    { text: "Has bumper stickers nobody asked for", severity: "low" },
    { text: "Revs engine at red lights", severity: "medium" },
    { text: "Parks across two spaces", severity: "high" },
    { text: "Has opinions about other people's cars", severity: "medium" },
    { text: "Thinks louder equals better", severity: "low" },
    { text: "He thinks he is tall", severity: "medium" },
    { text: "Lunch 2.0", severity: "high" }
];

// --- Subtle hover blip (Web Audio; lazily created, gracefully degrades) ---
let audioCtx = null;
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function playHoverBlip() {
    if (prefersReducedMotion.matches || readSaved('site-muted') === 'true') return; // honor reduced-motion / reduced-noise intent
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        if (!audioCtx) audioCtx = new Ctx();
        if (audioCtx.state === 'suspended') audioCtx.resume();

        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.type = 'sine';
        osc.frequency.value = 440;
        gain.gain.setValueAtTime(0.04, audioCtx.currentTime); // intentionally quiet
        gain.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + 0.08);
        osc.connect(gain).connect(audioCtx.destination);
        osc.start();
        osc.stop(audioCtx.currentTime + 0.09);
    } catch {
        // Audio unavailable — silently skip.
    }
}

function attachHoverBlip(el) {
    el.addEventListener('mouseenter', playHoverBlip);
}

// --- Jim image interaction -----------------------------------------------
function handleJimActivation() {
    jimClickCount++;
    totalJimClicks++;
    save('jim-total-clicks', totalJimClicks);

    if (jimAudio && readSaved('site-muted') !== 'true') {
        jimAudio.currentTime = 0;
        jimAudio.play().catch(() => {});
    }

    updateClickDisplay();

    if (clickCounter) {
        clickCounter.style.color = `hsl(${jimClickCount * 10}, 70%, 50%)`;
    }

    // Make the defect log dance briefly
    document.querySelectorAll('.jim-list li').forEach(item => {
        item.style.animation = 'dance 0.5s ease-in-out';
        setTimeout(() => { item.style.animation = ''; }, 500);
    });

    if (jimClickCount === SPECIAL_ANIMATION_CLICKS && jimImg) {
        jimImg.style.animation = 'spin 1s ease-in-out, float 2s ease-in-out infinite 1s';
    }

    if (jimClickCount === PARTY_MODE_CLICKS) {
        activatePartyMode();
        jimClickCount = 0;
        updateClickDisplay();
    }
}

function updateClickDisplay() {
    if (clickCounter) {
        clickCounter.textContent = jimClickCount;
        clickCounter.hidden = jimClickCount === 0;
    }
    if (clickDisplay) clickDisplay.textContent = totalJimClicks;
}

if (jimImg) {
    jimImg.addEventListener('click', handleJimActivation);
    // Keyboard support (it carries role="button" / tabindex="0")
    jimImg.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleJimActivation();
        }
    });
}

// --- Konami code ---------------------------------------------------------
document.addEventListener('keydown', (e) => {
    if (e.target.closest('input, textarea, select')) return;
    konamiSequence.push(e.code);
    konamiSequence = konamiSequence.slice(-konamiCode.length);
    if (konamiSequence.join(',') === konamiCode.join(',')) {
        activatePartyMode();
        konamiSequence = [];
    }
});

// --- Add a defect --------------------------------------------------------
if (addIssueBtn && issuesList) {
    addIssueBtn.addEventListener('click', function () {
        if (additionalJimIssues.length === 0) return;

        const randomIndex = Math.floor(Math.random() * additionalJimIssues.length);
        const newIssue = additionalJimIssues.splice(randomIndex, 1)[0];

        const li = document.createElement('li');
        li.className = `severity-${newIssue.severity}`;
        li.textContent = newIssue.text;
        li.style.opacity = '0';
        li.style.transform = 'translateX(-20px)';
        attachHoverBlip(li);
        issuesList.appendChild(li);

        // Animate in on the next frame
        requestAnimationFrame(() => {
            li.style.transition = 'all 0.3s ease';
            li.style.opacity = '1';
            li.style.transform = 'translateX(0)';
        });

        updateCriticalIssuesCount();

        const totalIssues = document.querySelectorAll('.jim-list li').length;
        if (totalIssues % ISSUES_PER_PARTY === 0) activatePartyMode();

        if (additionalJimIssues.length === 0) {
            this.disabled = true;
            this.textContent = 'Defect backlog exhausted — Jim is beyond repair';
        }
    });
}

function updateCriticalIssuesCount() {
    if (!issueCount) return;
    issueCount.textContent = document.querySelectorAll('.severity-high').length;
    issueCount.classList.add('pulse');
    setTimeout(() => issueCount.classList.remove('pulse'), 1000);
}

// Hover blip on the initial defect entries
document.querySelectorAll('.jim-list li').forEach(attachHoverBlip);

// --- Title hover: glitch + occasional balloons ---------------------------
if (mainTitle) {
    let hoverCount = 0;
    mainTitle.addEventListener('mouseenter', () => {
        mainTitle.classList.add('glitch-effect');
        setTimeout(() => mainTitle.classList.remove('glitch-effect'), 500);
        hoverCount++;
        if (hoverCount % 3 === 0) createMiniBalloons();
    });
}

// --- "Days since last incident" (resets for comedy) ----------------------
let daysSinceIncident = 0;
setInterval(() => {
    daysSinceIncident = daysSinceIncident === 0 ? Math.floor(Math.random() * 3) + 1 : 0;
    if (daysCounter) daysCounter.textContent = daysSinceIncident;
}, INCIDENT_RESET_MS);

// --- Colorblind-unfriendly mode (list item, header button, big red button)
(function setupColorblindToggles() {
    const headerBtn = document.getElementById('colorblind-toggle');

    const toggleMode = () => {
        const on = document.body.classList.toggle('colorblind-mode');
        if (headerBtn) headerBtn.setAttribute('aria-pressed', String(on));
        if (!on) return;

        if (jimAudio && readSaved('site-muted') !== 'true') {
            jimAudio.currentTime = 0;
            jimAudio.play().catch(() => {});
        }
        if (!prefersReducedMotion.matches) {
            confetti({
                particleCount: 50,
                spread: 90,
                colors: ['#ff0000', '#00ff00', '#ff0000', '#00ff00'],
                origin: { y: 0.5 }
            });
        }
        setTimeout(activatePartyMode, 500);
    };

    document.getElementById('colorblind-item')?.addEventListener('click', toggleMode);
    headerBtn?.addEventListener('click', toggleMode);

    document.getElementById('cool-mode-btn')?.addEventListener('click', function () {
        toggleMode();
        this.style.transform = 'scale(0.95)';
        setTimeout(() => { this.style.transform = ''; }, 100);
    });
})();

// --- Mini balloons (lightweight, center area) ----------------------------
function createMiniBalloons() {
    if (prefersReducedMotion.matches) return;
    const container = document.createElement('div');
    container.style.cssText = 'position:fixed; inset:0; z-index:999; pointer-events:none; overflow:hidden;';
    document.body.appendChild(container);

    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#6c5ce7'];
    const amount = 3;
    for (let i = 0; i < amount; i++) {
        setTimeout(() => {
            const balloon = document.createElement('div');
            const color = colors[Math.floor(Math.random() * colors.length)];
            const size = 30 + Math.random() * 20;
            const x = (window.innerWidth * 0.3) + (Math.random() * window.innerWidth * 0.4);
            balloon.style.cssText = `position:absolute; left:${x}px; bottom:-100px; width:${size}px; height:${size}px; background:${color}; border-radius:50%; opacity:0.8; animation: professional-balloon-float ${4 + Math.random() * 2}s ease-out forwards;`;
            container.appendChild(balloon);
        }, i * 100);
    }
    setTimeout(() => container.remove(), 6000);
}

// --- Footer year ---------------------------------------------------------
const footerYear = document.getElementById('footer-year');
if (footerYear) footerYear.textContent = new Date().getFullYear();

// --- Initialize ----------------------------------------------------------
updateCriticalIssuesCount();

updateClickDisplay();
